import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import {
  getUserPeriods,
  addPeriod,
  updatePeriod,
  deletePeriod,
  getUserSymptoms,
  upsertSymptom,
  deleteSymptom,
  calculateCycleStats,
  getCurrentPhase,
  checkPeriodNotification
} from '../services/cycleTrackerService';
import { updateCyclePhaseNotified } from '../utils/notificationManager';

const CycleTracker = () => {
  const [cycles, setCycles] = useState([]);
  const [symptoms, setSymptoms] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [showSymptomModal, setShowSymptomModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodLength, setPeriodLength] = useState(5);
  const [flow, setFlow] = useState('medium');
  const [symptomDate, setSymptomDate] = useState('');
  const [skinCondition, setSkinCondition] = useState('');
  const [energyLevel, setEnergyLevel] = useState(3);
  const [mood, setMood] = useState([]);
  const [cravings, setCravings] = useState([]);
  const [painLevel, setPainLevel] = useState(0);
  const [notes, setNotes] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [fabMenuOpen, setFabMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadCycleData();
  }, []);

  // Recalculate stats whenever cycles change
  useEffect(() => {
    if (cycles.length > 0) {
      const calculatedStats = calculateCycleStats(cycles);
      setStats(calculatedStats);
    } else {
      setStats(null);
    }
  }, [cycles]);

  // Load all cycle data from Supabase
  const loadCycleData = async () => {
    setLoading(true);
    try {
      // Load periods
      const { data: periodsData, error: periodsError } = await getUserPeriods();
      if (periodsError) throw periodsError;
      
      // Transform Supabase data to match component format
      const transformedPeriods = periodsData?.map(period => ({
        id: period.id,
        startDate: period.start_date,
        length: period.length,
        flow: period.flow,
        createdAt: period.created_at
      })) || [];
      
      setCycles(transformedPeriods);

      // Load symptoms
      const { data: symptomsData, error: symptomsError } = await getUserSymptoms();
      if (symptomsError) throw symptomsError;
      
      // Transform symptoms array to object keyed by date
      const symptomsObj = {};
      symptomsData?.forEach(symptom => {
        symptomsObj[symptom.symptom_date] = {
          id: symptom.id,
          date: symptom.symptom_date,
          skinCondition: symptom.skin_condition,
          energyLevel: symptom.energy_level,
          mood: symptom.mood || [],
          cravings: symptom.cravings || [],
          painLevel: symptom.pain_level,
          notes: symptom.notes,
          createdAt: symptom.created_at
        };
      });
      
      setSymptoms(symptomsObj);

      // Check for notifications after loading data
      if (transformedPeriods.length > 0) {
        checkForUpcomingPeriod(transformedPeriods);
      }
    } catch (error) {
      console.error('Error loading cycle data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check for upcoming period and fertility notifications
  const checkForUpcomingPeriod = (periodsData) => {
    if (!periodsData || periodsData.length === 0) return;
    
    const stats = calculateCycleStats(periodsData);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to midnight for accurate day comparison
    
    // Check for fertility window (ovulation)
    if (stats.lastPeriod) {
      const lastPeriodDate = new Date(stats.lastPeriod);
      lastPeriodDate.setHours(0, 0, 0, 0); // Reset to midnight
      
      const daysSinceLastPeriod = Math.floor((today - lastPeriodDate) / (1000 * 60 * 60 * 24));
      const cycleDay = daysSinceLastPeriod + 1;
      const cycleLength = stats.avgCycleLength || 28;
      
      // Calculate phase boundaries (same as cycleTrackerService)
      // For 28-day cycle: follicular ends day 13, ovulation ends day 16
      const follicularEnd = Math.round(cycleLength * 0.46); // ~Day 13
      const ovulationEnd = Math.round(cycleLength * 0.57); // ~Day 16
      
      // Ovulation phase notification (days 14-16 for 28-day cycle)
      if (cycleDay > follicularEnd && cycleDay <= ovulationEnd) {
        const ovulationDay = cycleDay - follicularEnd;
        setNotificationMessage(`Ovulation phase day ${ovulationDay} - peak energy & fertility window.`);
        setShowNotificationBanner(true);
        
        try {
          localStorage.setItem('cycleNotification', JSON.stringify({
            type: 'fertility',
            message: 'Ovulation phase',
            date: new Date().toISOString()
          }));
        } catch (e) {
          console.error('Failed to save notification', e);
        }
        return;
      }
    }
    
    // Check for period notifications
    const notification = checkPeriodNotification(stats);
    
    if (notification && notification.show) {
      // Customize message based on days until period
      let message = notification.message;
      if (notification.daysUntil <= 3 && notification.daysUntil > 0) {
        message = `🩸 Your period starts in ${notification.daysUntil} day${notification.daysUntil > 1 ? 's' : ''}! Time to prep supplies.`;
      } else if (notification.daysUntil === 0) {
        message = '🩸 Your period is expected today!';
      }
      
      setNotificationMessage(message);
      setShowNotificationBanner(true);
      
      // Store notification in localStorage for dashboard
      try {
        localStorage.setItem('cycleNotification', JSON.stringify({
          type: 'period',
          message: message,
          date: new Date().toISOString(),
          daysUntil: notification.daysUntil
        }));
      } catch (e) {
        console.error('Failed to save notification', e);
      }
    } else {
      setShowNotificationBanner(false);
      localStorage.removeItem('cycleNotification');
    }
  };

  const handleAddPeriod = async (e) => {
    e.preventDefault();
    try {
      if (editingPeriod) {
        // Update existing period
        const { data, error } = await updatePeriod(editingPeriod.id, {
          startDate: periodStartDate,
          length: periodLength,
          flow: flow
        });

        if (error) throw error;
      } else {
        // Add new period
        const { data, error } = await addPeriod({
          startDate: periodStartDate,
          length: periodLength,
          flow: flow
        });

        if (error) throw error;
      }

      // Reload data to get updated list
      await loadCycleData();
      
      setPeriodStartDate('');
      setPeriodLength(5);
      setFlow('medium');
      setEditingPeriod(null);
      setShowPeriodModal(false);
    } catch (error) {
      console.error('Error saving period:', error);
      alert('Failed to save period. Please try again.');
    }
  };

  const openEditPeriod = (period) => {
    setEditingPeriod(period);
    setPeriodStartDate(period.startDate);
    setPeriodLength(period.length);
    setFlow(period.flow);
    setShowPeriodModal(true);
  };

  const openAddPeriod = () => {
    setEditingPeriod(null);
    setPeriodStartDate('');
    setPeriodLength(5);
    setFlow('medium');
    setShowPeriodModal(true);
  };

  const handleAddSymptoms = async (e) => {
    e.preventDefault();
    try {
      const { data, error } = await upsertSymptom({
        date: symptomDate,
        skinCondition,
        energyLevel,
        mood,
        cravings,
        painLevel,
        notes
      });

      if (error) throw error;

      // Reload data to get updated list
      await loadCycleData();
      
      setSymptomDate('');
      setSkinCondition('');
      setEnergyLevel(3);
      setMood([]);
      setCravings([]);
      setPainLevel(0);
      setNotes('');
      setShowSymptomModal(false);
    } catch (error) {
      console.error('Error adding symptoms:', error);
      alert('Failed to add symptoms. Please try again.');
    }
  };

  const handleDeletePeriod = async (id) => {
    try {
      const { error } = await deletePeriod(id);
      if (error) throw error;
      
      // Reload data to get updated list
      await loadCycleData();
    } catch (error) {
      console.error('Error deleting period:', error);
      alert('Failed to delete period. Please try again.');
    }
  };

  const handleDeleteSymptom = async (date) => {
    try {
      const { error } = await deleteSymptom(date);
      if (error) throw error;
      
      // Reload data to get updated list
      await loadCycleData();
    } catch (error) {
      console.error('Error deleting symptom:', error);
      alert('Failed to delete symptom. Please try again.');
    }
  };

  const currentPhase = getCurrentPhase(stats);

  // Update cycle phase notification tracking when user views this page
  useEffect(() => {
    if (currentPhase?.phase) {
      updateCyclePhaseNotified(currentPhase.phase);
    }
  }, [currentPhase?.phase]);

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  };

  const calendarDays = generateCalendar();

  const getDateInfo = (day) => {
    if (!day) return null;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const checkDate = new Date(dateStr);
    
    // Check if this is today
    const today = new Date();
    const isToday = checkDate.getDate() === today.getDate() && 
                    checkDate.getMonth() === today.getMonth() && 
                    checkDate.getFullYear() === today.getFullYear();
    
    // Check for actual logged periods
    const hasPeriod = cycles.some(c => {
      const start = new Date(c.startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + c.length);
      return checkDate >= start && checkDate < end;
    });
    
    // Check for predicted period (only if we have stats)
    let isPredictedPeriod = false;
    if (stats && stats.nextPredicted) {
      const predictedStart = new Date(stats.nextPredicted);
      const predictedEnd = new Date(predictedStart);
      // Use actual average period length, default to 5 if not available
      const periodLength = stats.avgPeriodLength || 5;
      predictedEnd.setDate(predictedEnd.getDate() + periodLength);
      isPredictedPeriod = checkDate >= predictedStart && checkDate < predictedEnd;
    }
    
    // Determine cycle phase for this day
    let phase = null;
    let isOvulation = false;
    let isFollicular = false;
    let isLuteal = false;
    
    if (stats && stats.lastPeriod && !hasPeriod && !isPredictedPeriod) {
      const daysSinceLastPeriod = Math.floor((checkDate - stats.lastPeriod) / (1000 * 60 * 60 * 24));
      const cycleDay = daysSinceLastPeriod + 1;
      const cycleLength = stats.avgCycleLength || 28;
      const periodLength = stats.avgPeriodLength || 5;
      
      // Calculate phase boundaries based on cycle length
      // For 28-day: menstrual 1-5, follicular 6-13, ovulation 14-16, luteal 17-28
      const menstrualEnd = periodLength; // Day 5
      const ovulationStart = cycleLength - 14; // Day 14 for 28-day (ovulation happens ~14 days before next period)
      const ovulationEnd = ovulationStart + 2; // 3 days: 14, 15, 16
      
      // Only show phases within the current cycle (not beyond expected length + buffer)
      if (cycleDay > 0 && cycleDay <= cycleLength + 7) {
        if (cycleDay > menstrualEnd && cycleDay < ovulationStart) {
          phase = 'follicular';
          isFollicular = true;
        } else if (cycleDay >= ovulationStart && cycleDay <= ovulationEnd) {
          phase = 'ovulation';
          isOvulation = true;
        } else if (cycleDay > ovulationEnd) {
          phase = 'luteal';
          isLuteal = true;
        }
      }
    }
    
    const hasSymptoms = symptoms[dateStr];
    return { hasPeriod, isPredictedPeriod, hasSymptoms, dateStr, isToday, isOvulation, isFollicular, isLuteal, phase };
  };

  const getSkinInsights = () => {
    const phase = currentPhase.phase;
    const insights = {
      'Menstrual Phase': {
        title: 'Menstrual Phase Skin Care',
        tips: [
          'Skin may be more sensitive and dry',
          'Focus on gentle, hydrating products',
          'Avoid harsh exfoliants',
          'Increase omega-3 intake for inflammation'
        ],
        color: 'text-red-600',
        bg: 'bg-red-50'
      },
      'Follicular Phase': {
        title: 'Follicular Phase Skin Care',
        tips: [
          'Skin is typically clearer and glowing',
          'Great time to try new products',
          'Collagen production is higher',
          'Exfoliate to maximize glow'
        ],
        color: 'text-green-600',
        bg: 'bg-green-50'
      },
      'Ovulation Phase': {
        title: 'Ovulation Phase Skin Care',
        tips: [
          'Skin is at its best - radiant and clear',
          'Oil production may increase slightly',
          'Stay hydrated',
          'Light moisturizers work well'
        ],
        color: 'text-pink-600',
        bg: 'bg-pink-50'
      },
      'Luteal Phase': {
        title: 'Luteal Phase Skin Care',
        tips: [
          'Increased oil production and breakouts',
          'Use gentle acne treatments',
          'Avoid dairy and sugar',
          'Clay masks can help control oil'
        ],
        color: 'text-purple-600',
        bg: 'bg-purple-50'
      }
    };
    return insights[phase] || null;
  };

  const skinInsights = getSkinInsights();
  const moodOptions = ['Happy', 'Anxious', 'Irritable', 'Calm', 'Sad', 'Energetic', 'Tired'];
  const cravingOptions = ['Sweet', 'Salty', 'Carbs', 'Chocolate', 'None'];
  const skinOptions = ['Clear', 'Breakouts', 'Dry', 'Oily', 'Sensitive', 'Glowing'];

  const toggleMood = (option) => {
    setMood(prev => prev.includes(option) ? prev.filter(m => m !== option) : [...prev, option]);
  };

  const toggleCraving = (option) => {
    setCravings(prev => prev.includes(option) ? prev.filter(c => c !== option) : [...prev, option]);
  };

  // Calculate current cycle day
  const getCurrentCycleDay = () => {
    if (!stats || !stats.lastPeriod) return null;
    const today = new Date();
    const daysSinceLastPeriod = Math.floor((today - stats.lastPeriod) / (1000 * 60 * 60 * 24));
    return daysSinceLastPeriod + 1;
  };

  const currentCycleDay = getCurrentCycleDay();

  return (
    <div className="min-h-screen bg-gradient-to-br from-beige-50 via-rose-50 to-beige-100 pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <RouterLink to="/dashboard" className="inline-flex items-center text-charcoal-600 hover:text-charcoal-800 mb-4 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </RouterLink>
          <div className="text-center">
            <h1 className="text-4xl font-playfair font-semibold text-charcoal-900 mb-2">Track Your Cycle</h1>
            <p className="text-charcoal-600 max-w-2xl mx-auto">Understand your body's rhythm, identify patterns, and support your wellbeing naturally.</p>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mb-4"></div>
              <p className="text-charcoal-600">Loading your cycle data...</p>
            </div>
          </div>
        ) : (
          <>
            {showNotificationBanner && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }} 
                animate={{ opacity: 1, y: 0 }} 
                className={`${
                  notificationMessage.includes('fertile') || notificationMessage.includes('fertility') 
                    ? 'bg-gradient-to-r from-pink-500 to-pink-600' 
                    : 'bg-gradient-to-r from-rose-500 to-rose-600'
                } text-white p-4 rounded-2xl shadow-lg mb-6 flex items-center justify-between`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium">{notificationMessage}</span>
                </div>
                <button 
                  onClick={() => setShowNotificationBanner(false)} 
                  className="text-white hover:text-white/80 transition-colors"
                >
                  ✕
                </button>
              </motion.div>
            )}

            {/* SECTION 1: Current Phase Overview */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <div className={`${currentPhase.bg} p-6 rounded-3xl shadow-soft border border-white/50`}>
                {/* Progress Bar Section */}
                {currentPhase.dayInPhase && currentPhase.totalPhaseDays && (
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-charcoal-600">
                        Phase Progress: Day {currentPhase.dayInPhase} of {currentPhase.totalPhaseDays}
                      </span>
                      <span className="text-xs text-charcoal-500">
                        {currentCycleDay ? `Cycle Day ${currentCycleDay}` : ''}
                        {stats && currentCycleDay ? ` / ${stats.avgCycleLength}` : ''}
                      </span>
                    </div>
                    <div className="w-full bg-white/50 rounded-full h-2.5 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-300 ${
                          currentPhase.phase === 'Menstrual Phase' ? 'bg-red-400' :
                          currentPhase.phase === 'Follicular Phase' ? 'bg-green-400' :
                          currentPhase.phase === 'Ovulation Phase' ? 'bg-pink-400' :
                          currentPhase.phase === 'Luteal Phase' ? 'bg-purple-400' :
                          'bg-gray-400'
                        }`}
                        style={{ width: `${(currentPhase.dayInPhase / currentPhase.totalPhaseDays) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Phase Info - Horizontal Layout */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Phase Name & Icon */}
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{currentPhase.icon}</span>
                    <div>
                      <div className={`text-2xl font-bold ${currentPhase.color}`}>{currentPhase.phase}</div>
                      {currentPhase.description && (
                        <p className="text-sm text-charcoal-600 mt-1 max-w-md">{currentPhase.description}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Cycle Stats */}
                  {stats && (
                    <div className="flex flex-col gap-2 md:text-right">
                      <div className="text-sm text-charcoal-600">
                        <span className="font-semibold">Avg Cycle:</span> {stats.avgCycleLength} days
                      </div>
                      {stats.avgPeriodLength && (
                        <div className="text-sm text-charcoal-600">
                          <span className="font-semibold">Avg Period:</span> {stats.avgPeriodLength} days
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

        {/* SECTION 2: Calendar */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/90 backdrop-blur-sm p-6 rounded-3xl shadow-soft mb-8 border border-beige-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-charcoal-900">Calendar View</h2>
              <div className="flex items-center gap-4">
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">←</button>
                <span className="text-lg font-medium text-charcoal-800">{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">→</button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs sm:text-sm font-semibold text-charcoal-600 py-2">{day}</div>
              ))}
              {calendarDays.map((day, idx) => {
                const info = getDateInfo(day);
                return (
                  <div 
                    key={idx} 
                    onClick={() => {
                      if (day && info?.dateStr) {
                        setSymptomDate(info.dateStr);
                        setShowSymptomModal(true);
                      }
                    }}
                    className={`aspect-square p-1 sm:p-2 rounded-lg text-center text-xs sm:text-sm relative overflow-hidden ${
                      !day ? 'bg-transparent' : 
                      info?.hasPeriod ? 'bg-red-100 border-2 border-red-300 font-semibold text-red-700 cursor-pointer hover:bg-red-200' : 
                      info?.isPredictedPeriod ? 'bg-red-50 border-2 border-red-200 border-dashed font-medium text-red-600 cursor-pointer hover:bg-red-100' :
                      info?.isOvulation ? 'bg-pink-100 border-2 border-pink-400 font-semibold text-pink-700 cursor-pointer hover:bg-pink-200' :
                      info?.hasSymptoms ? 'bg-amber-50 border border-amber-300 font-medium text-amber-700 cursor-pointer hover:bg-amber-100' : 
                      'bg-gray-50 hover:bg-gray-100 cursor-pointer'
                    }`}
                  >
                    {day}
                    {info?.isToday && (
                      <div className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-100 border-2 border-red-300 rounded"></div>
                <span className="text-charcoal-600">Period</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-50 border-2 border-red-200 border-dashed rounded"></div>
                <span className="text-charcoal-600">Predicted</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-pink-100 border-2 border-pink-400 rounded"></div>
                <span className="text-charcoal-600">Ovulation</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-50 border border-amber-300 rounded"></div>
                <span className="text-charcoal-600">Symptoms</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-50 border border-gray-200 rounded relative">
                  <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                </div>
                <span className="text-charcoal-600">Today</span>
              </div>
            </div>
          </motion.div>

        {/* SECTION 4: Cycle History */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8 mb-8">
          <div className="bg-white/90 backdrop-blur-sm p-6 rounded-3xl shadow-soft border border-beige-200">
            <h3 className="text-2xl font-semibold text-charcoal-900 mb-4">Period History</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {cycles.length === 0 ? (
                <p className="text-sm text-charcoal-500 text-center py-8">No periods logged yet</p>
              ) : (
                [...cycles].sort((a, b) => new Date(b.startDate) - new Date(a.startDate)).map(cycle => (
                  <div key={cycle.id} className="p-4 bg-red-50 rounded-xl">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-charcoal-900 break-words">{new Date(cycle.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                        <div className="text-sm text-charcoal-600">{cycle.length} days • {cycle.flow} flow</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => openEditPeriod(cycle)}
                          className="px-3 py-1.5 bg-white text-rose-600 rounded-lg hover:bg-rose-100 transition-colors text-sm font-medium border border-rose-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePeriod(cycle.id)}
                          className="px-3 py-1.5 bg-white text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium border border-red-300"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm p-6 rounded-3xl shadow-soft border border-beige-200">
            <h3 className="text-2xl font-semibold text-charcoal-900 mb-4">Symptom History</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {Object.keys(symptoms).length === 0 ? (
                <p className="text-sm text-charcoal-500 text-center py-8">No symptoms logged yet</p>
              ) : (
                Object.entries(symptoms).sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA)).map(([date, symptom]) => (
                  <div key={date} className="p-4 bg-purple-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-charcoal-900">{new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                      <button onClick={() => handleDeleteSymptom(date)} className="text-purple-500 hover:text-purple-700 text-sm">Delete</button>
                    </div>
                    <div className="space-y-1 text-sm text-charcoal-600">
                      {symptom.skinCondition && <div>Skin: <span className="font-medium">{symptom.skinCondition}</span></div>}
                      <div>Energy: <span className="font-medium">{symptom.energyLevel}/5</span></div>
                      {symptom.mood.length > 0 && <div>Mood: <span className="font-medium">{symptom.mood.join(', ')}</span></div>}
                      {symptom.painLevel > 0 && <div>Pain: <span className="font-medium">{symptom.painLevel}/10</span></div>}
                      {symptom.notes && <div className="mt-2 text-xs italic">{symptom.notes}</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </motion.div>

        {/* Disclaimer */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="text-center text-xs text-charcoal-500 max-w-3xl mx-auto px-4 pb-8">
          <p className="leading-relaxed">
            <span className="font-medium">Medical Disclaimer:</span> Cycle predictions and fertility estimates are calculated based on your tracked data and typical cycle patterns. Initial predictions use a standard 28-day cycle model. As you log more periods, predictions become increasingly personalized and accurate based on your unique cycle length and patterns. This tool is for informational purposes only and should not replace professional medical advice. Consult with a healthcare provider for medical concerns or family planning decisions.
          </p>
        </motion.div>

        {/* Expandable Floating Action Button Menu */}
        <div className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40">
          {/* Menu Options - appear when open */}
          {fabMenuOpen && (
            <>
              {/* Log Period Option */}
              <motion.button
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: 0.05 }}
                onClick={() => {
                  setFabMenuOpen(false);
                  openAddPeriod();
                }}
                className="absolute bottom-20 right-0 bg-white text-charcoal-800 px-4 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2 whitespace-nowrap group"
              >
                <span className="text-xl">🩸</span>
                <span className="font-medium">Log Period</span>
              </motion.button>

              {/* Log Symptoms Option */}
              <motion.button
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: 0.1 }}
                onClick={() => {
                  setFabMenuOpen(false);
                  setSymptomDate(new Date().toISOString().split('T')[0]);
                  setShowSymptomModal(true);
                }}
                className="absolute bottom-36 right-0 bg-white text-charcoal-800 px-4 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2 whitespace-nowrap group"
              >
                <span className="text-xl">📝</span>
                <span className="font-medium">Log Symptoms</span>
              </motion.button>

              {/* Background overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setFabMenuOpen(false)}
                className="fixed inset-0 -z-10"
              />
            </>
          )}

          {/* Main FAB Button */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              rotate: fabMenuOpen ? 45 : 0
            }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setFabMenuOpen(!fabMenuOpen)}
            className="bg-gradient-to-r from-rose-500 to-rose-600 text-white p-4 md:p-5 rounded-full shadow-xl hover:shadow-2xl hover:from-rose-600 hover:to-rose-700 transition-all"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
          </motion.button>
        </div>

        {showPeriodModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl shadow-xl p-6 max-w-md w-full">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold text-charcoal-900">{editingPeriod ? 'Edit Period' : 'Log Period'}</h2>
                <button onClick={() => setShowPeriodModal(false)} className="text-charcoal-400 hover:text-charcoal-600">✕</button>
              </div>
              <form onSubmit={handleAddPeriod} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Start Date</label>
                  <input type="date" value={periodStartDate} onChange={(e) => setPeriodStartDate(e.target.value)} required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent bg-white text-charcoal-900 appearance-none" style={{ colorScheme: 'light', WebkitAppearance: 'none' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Period Length (days)</label>
                  <input type="number" min="1" max="10" value={periodLength} onChange={(e) => setPeriodLength(parseInt(e.target.value))} required className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Flow Intensity</label>
                  <select value={flow} onChange={(e) => setFlow(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent">
                    <option value="light">Light</option>
                    <option value="medium">Medium</option>
                    <option value="heavy">Heavy</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowPeriodModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-xl text-charcoal-700 hover:bg-gray-50 transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-rose-500 text-white rounded-xl hover:bg-rose-600 transition-colors">{editingPeriod ? 'Update Period' : 'Save Period'}</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {showSymptomModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40">
            <div className="flex min-h-full items-center justify-center p-4">
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl shadow-xl p-6 max-w-2xl w-full my-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold text-charcoal-900">Log Daily Symptoms</h2>
                <button onClick={() => setShowSymptomModal(false)} className="text-charcoal-400 hover:text-charcoal-600">✕</button>
              </div>
              <form onSubmit={handleAddSymptoms} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Date</label>
                  <input type="date" value={symptomDate} onChange={(e) => setSymptomDate(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Skin Condition</label>
                  <select value={skinCondition} onChange={(e) => setSkinCondition(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                    <option value="">Select condition</option>
                    {skinOptions.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Energy Level: {energyLevel}/5</label>
                  <input type="range" min="1" max="5" value={energyLevel} onChange={(e) => setEnergyLevel(parseInt(e.target.value))} className="w-full" />
                  <div className="flex justify-between text-xs text-charcoal-500 mt-1">
                    <span>Low</span>
                    <span>High</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Mood (select all that apply)</label>
                  <div className="flex flex-wrap gap-2">
                    {moodOptions.map(option => (
                      <button key={option} type="button" onClick={() => toggleMood(option)} className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${mood.includes(option) ? 'bg-purple-500 text-white' : 'bg-gray-100 text-charcoal-700 hover:bg-gray-200'}`}>{option}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Cravings</label>
                  <div className="flex flex-wrap gap-2">
                    {cravingOptions.map(option => (
                      <button key={option} type="button" onClick={() => toggleCraving(option)} className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${cravings.includes(option) ? 'bg-purple-500 text-white' : 'bg-gray-100 text-charcoal-700 hover:bg-gray-200'}`}>{option}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Pain Level: {painLevel}/10</label>
                  <input type="range" min="0" max="10" value={painLevel} onChange={(e) => setPainLevel(parseInt(e.target.value))} className="w-full" />
                  <div className="flex justify-between text-xs text-charcoal-500 mt-1">
                    <span>None</span>
                    <span>Severe</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Notes (optional)</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="Any additional notes..."></textarea>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowSymptomModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-xl text-charcoal-700 hover:bg-gray-50 transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors">Save Symptoms</button>
                </div>
              </form>
            </motion.div>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default CycleTracker;
